import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { AccessTime, AddBox, CheckCircle, InsertDriveFile, Launch, Send, ContentPasteOffOutlined, PendingActions, Note, FolderShared } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, IconButton, Modal, Tooltip, Typography } from "@mui/material"
import { GridColDef } from '@mui/x-data-grid';
import { format, parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime } from "date-fns-tz"
import { ADD_STUDENT_NOTE_MUTATION, AddStudentNoteMutation, AddStudentNoteMutationVariables, canLaunchSingle, canMakeReferral, convert_enum_to_string, NoteOriginType, NoteTypeEnum, ParentalConsentType, Session, Student, StudentDataType, Subscription } from "ft-shared-graphql";
import { Table } from '../../../../components/Table';
import SimpleToolbar from '../../../../components/Table/SimpleToolbar';
import { useStudentContext } from '../../../../context/studentContext';
import { markReviewed } from '../../../../features/graphql/mutation/markReviewed/markReviewed';
// import { useSlideInContext } from '../../../../context/SlideInContext';
import { useSlideInContext, useManageModal, HeaderActionType } from 'ui';
import { ADD_NOTE, LAUNCH_FOR_OTHERS, LAUNCH_FOR_SELF, LaunchRequest, MAKE_A_REFERRAL, PARENT_EMAIL_ACTION, PARENT_OPTION_ACTION, PARENTAL_CONSENT, ParentalConsentRequest, PREVIEW_EMAIL_INFO, SEND_TO_PARENT_OPTIONS, TRUESAGE_FILE } from 'ft-shared-components';
import { useNavigate, useParams } from 'react-router';
import { gql } from '../../../../__generated__';
import { LoadingWrapper } from 'ui';
// import { LoadingWrapper } from '../../../../components/LoadingWrapper';
import { NetworkError } from '../../../../components/NetworkError';
import { TagsCell } from './TagCell';
// import { TagsCell } from 'ui';
import CheckpointCell from './CheckpointCell';
import usePersonStore from '../../../store/personStore';
import usePersonAuthStore, { decodedToken } from "../../../store/personAuthStore"
import useSelectStudent from '../../../StudentPages/hooks/useSelectedStudent';
export const STUDENT_SESSIONS_QUERY = gql(/* GraphQL */`
  query StudentSessions($studentId: UUID!) {
    student(id: $studentId) {
      __typename
      id
      firstName
      lastName
      parentEmail
      email
      dob
      expired
      parentalConsentStatus
      lastConsentAction{
        id
        type
        createdAt
      }
      consentEmailSentTimes
      sessions(status: null) {
        __typename
        id
        category
        data
        riskRating
        status
        type
        hasKeyWords
        checkpoint {
          current {
            position
            name
          }
          values
        }
        createdAt
        launchPerson {
          firstName
          lastName
        }
        worklist {
          id
          status
        }
        report {
          id
          category
          createdAt
        }
        notes {
          id
          type
          body
          createdAt
          person {
            id
            firstName
            lastName
            role {
              normalizedName
            }
          }
        }
        history {
          data
          createdAt
        }
      }
    }
  }
`);

const sortDate = (order: 'asc' | 'desc') => (a: any, b: any) => {
  const aTime = new Date(a.createdAt).getTime();
  const bTime = new Date(b.createdAt).getTime();
  return order === 'asc' ? aTime - bTime : bTime - aTime;
}

function mapTableData(session: Session, student: Student) {
  let _dateFormat = (v: string | null | undefined, f = 'MM/dd/yyyy HH:mm') => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return !v
      ? null
      : v.lastIndexOf('Z') > 0
        ? format(toZonedTime(parseISO(v), timeZone), f)
        : format(fromZonedTime(parseISO(v), ''), f);
  };

  const history = [...session.history].sort(sortDate('desc'))[0];
  const checkpoint = session.checkpoint?.current?.name;
  const partialReportCheckpoints = ['EnvironmentQuestions', 'PersonalQuestions', 'AdventureSelected', "FamilyQuestions", "DescribeAdultsQuestions", "PersonalSupportQuestions", "PersonalProblemsQuestions", "HappyOrSadQuestions", "TiredOrAwakeQuestions", "HobbiesQuestions", "AtSchoolQuestions", "PhysicalSignsQuestions", "WorryQuestions"]

  const historyDateFormated = _dateFormat(history?.createdAt) ?? "";
  let decodedStatus = history?.data?.label === 'Created'
    ? `Created \n${historyDateFormated}`
    : history?.data?.label === 'Opened'
      ? `Opened \n${historyDateFormated}`
      : history?.data?.label === 'AuthenticationSuccess' || history?.data?.label === 'AvatarSelected'
        ? `Started \n ${historyDateFormated}`
        : session.status === 'Complete' && (partialReportCheckpoints.includes(checkpoint ?? ''))
          ? `Partial Report`
          : session.status === 'Complete' && checkpoint === 'AdventureComplete'
            ? 'Full Report'
            : null;

  if (decodedStatus === null) {
    const authHistoryRecord = session.history.find((x: any) => x.data.label === 'AuthenticationSuccess');
    // console.log("the auth record is: ", session, "and the checkpoint is: ", checkpoint)
    // Encase a history record is not found (should never be the case)
    decodedStatus = authHistoryRecord
      ? `Started \n ${_dateFormat(authHistoryRecord?.createdAt)}`
      : 'Created';
  }
  return {
    id: session.id,
    studentId: student.id,
    category: session.category,
    checkpoint: checkpoint,
    data: session.data,
    notes: session.notes,
    worklist: session.worklist,
    report: session.report,
    status: decodedStatus,
    hasKeyWords: session.hasKeyWords,
    sessionType: `${session.category} \n${session.type}`,
    sessionTypeRaw: session.type,
    date: `${_dateFormat(session.createdAt, 'MM/dd/yyyy')}`,
    launchedBy: `By: ${session.launchPerson.firstName} ${session.launchPerson.lastName} \nOn: ${_dateFormat(session.createdAt, 'MM/dd/yyyy')}`,
  }
}

const modalstyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: "5px",
  boxShadow: 24,
  p: 4,
};

const MarkSessionCell = ({ id, value, handleMark }: { id: string, value: any, handleMark: (id: string) => void }) => {
  if (!value) {
    return (
      <Tooltip title='There is nothing to review' >
        <IconButton>
          <ContentPasteOffOutlined />
        </IconButton>
      </Tooltip >
    )
  }
  if (value === "Unread") {
    return (
      <Tooltip title="Mark Reviewed">
        <IconButton onClick={() => handleMark(id)}>
          <PendingActions sx={{ color: "#ffc107" }} />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Tooltip title="Reviewed">
      <IconButton onClick={() => { }} disableFocusRipple sx={{ cursor: "default" }}>
        <CheckCircle sx={{ color: "#4caf50" }} />
      </IconButton>
    </Tooltip>
  )
};

const ViewReportCell = ({ value }: { value: any }) => {
  const [addStudentNoteMutation] = useMutation<AddStudentNoteMutation, AddStudentNoteMutationVariables>(ADD_STUDENT_NOTE_MUTATION);
  const [MarkReviewed, { loading }] = useMutation(markReviewed);
  const onClick = async () => {
    const response_sessionNote = await addStudentNoteMutation({
      variables: {
        studentId: value.studentId,
        type: NoteTypeEnum.SessionNote,
        origin: NoteOriginType.ResultsTable,
        body: `Viewed Report PDF for session completed on: ${value.date}`
      },
    })
    if (value?.worklist?.status == 'Unread') {
      const response_markRead = await MarkReviewed({ variables: { id: value.worklist.id } })
    }
    // Exit early if no report is found
    // Technically, this function shouldn't be used if no PDF is found
    // because the UI should disable the link/button from being clicked
    if (!Boolean(value.report.length)) return;
    //NOTE: USING FIND BC THERE SHOULD BE ONLY ONE REPORT PER SESSION
    const reportFile = value.report.find((file: { category: string }) => file.category === "Report")
    const link = document.createElement('a');
    link.href = `${window.__env.API_URL}/api/file/download/client/reports/${reportFile.id}`;
    link.target = "_blank"

    // Prevent potential memory leak and cleanup ObjectURL
    // resources that are no longer in use
    const linkHandler = () => {
      setTimeout(() => {
        link.removeEventListener('click', linkHandler);
        document.body.removeChild(link);
      }, 300);
    }

    link.addEventListener('click', linkHandler);
    document.body.appendChild(link);
    link.click()
  };

  return (
    // Cursed but converts the value to a boolean and negates it -- love JS :)
    <IconButton disabled={!!!value.report.length} onClick={onClick}>
      <InsertDriveFile />
    </IconButton>
  );
};

// const AddNoteCell = ({ value, roles }: { value: any, roles: string[] }) => {
//   const { handleContent } = useSlideInContext();
//   const { setSession } = useWorkListContext();
//   const { openModal } = useManageModal();
//   const handleAddNote = () => {
//     if (roles.includes("SUPPORT ADMIN")) {
//       // openModal({ type: "ROLE_PERMISSIONS_MISSING", cancelButtonLabel: "Close" })
//     } else {
//       console.log(value)
//       setSession(value);
//       handleContent(ADD_NOTE, {
//         sessionId: value.id
//       })
//     }
//   };
//   return (
//     <IconButton onClick={handleAddNote}>
//       <AddBox />
//     </IconButton>
//   );
// };

// const HistoryCell = ({
//   data,
//   onClick,
// }: {
//   data: any;
//   onClick: () => void;
// }) => {
//   return (
//     <IconButton disabled={!data?.notes} onClick={onClick}>
//       <AccessTime />
//     </IconButton>
//   );
// };

type ResultTableParams = {
  studentId: string
}

export default function ResultsTable({
  onBack,
  onSelectedSession,
  activeStudent
}: {
  onBack: () => void;
  onSelectedSession: (studentId: string, sessionId: string) => void,
  activeStudent: any
}) {

  const params = useParams<ResultTableParams>();
  const navigate = useNavigate()
  const { openModal } = useManageModal()
  const { handleContent, toggleDrawer, disableClosingDrawer } = useSlideInContext();
  const [MarkReviewed, { loading }] = useMutation(markReviewed);
  const { changeStudent, studentData, updateStudent } = useStudentContext();
  const { student } = useSelectStudent(params.studentId);
  const handleMark = (_id: string) => setMarkedId(_id)
  const personState = usePersonStore(state => state)
  const accessToken = usePersonAuthStore(state => state.access_token)
  const roles = usePersonAuthStore(state => state.roles)
  const [rows, setRows] = useState<any[]>([]);
  const [markedId, setMarkedId] = useState<null | string>(null);
  useEffect(() => {
    if (studentData != student) {
      changeStudent(student)
    }
  }, [student])
  const { data, loading: studentSessionsLoading, error, refetch } = useQuery(STUDENT_SESSIONS_QUERY, {
    fetchPolicy: 'cache-first',
    variables: {
      studentId: params.studentId
    }
  });
  useEffect(() => {
    // Reset player data when results table isn't mounted
    return () => changeStudent(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!data) return;
    const rows = data?.student.sessions.map(s => mapTableData(s as any, data.student as any));
    setRows(rows ?? []);
  }, [data]);

  const handleLaunch = () => {
    const studentConsentStatus = data?.student?.parentalConsentStatus?.toUpperCase() ?? null
    changeStudent({ id: params.studentId, parentEmail: data?.student.parentEmail, email: data?.student.email, dob: data?.student.dob, lastConsentAction: data?.student.lastConsentAction, parentalConsentStatus: data?.student.parentalConsentStatus, consentEmailSentTimes: data?.student.consentEmailSentTimes });
    if (personState.company.consentPolicy && (studentConsentStatus !== ParentalConsentType.Given && studentConsentStatus !== ParentalConsentType.Timelapsed)) {
      if (!studentConsentStatus) {
        handleContent(PARENTAL_CONSENT, {
          studentData: studentData as StudentDataType,
          updateStudent: updateStudent,
          companyId: personState.company.id!,
          onSubmit: handleSendParentalConsent
        })
      } else if (studentConsentStatus === ParentalConsentType.Pending) {
        handleContent(PARENTAL_CONSENT, {
          studentData: studentData as StudentDataType,
          updateStudent: updateStudent,
          companyId: personState.company.id!,
          onSubmit: handleSendParentalConsent
        })
      } else {
        openModal({ type: "PARENTAL_CONSENT_NOT_GIVEN", cancelButtonLabel: "close" })
      }
    } else {
      if (roles.includes("MEDICAL STANDARD") || roles.includes("MEDICAL ADMIN") || roles.includes("MEDICAL SUPER") || roles.includes("EDUCATION SUPER")) {
        handleContent(LAUNCH_FOR_OTHERS, {
          studentData: student as StudentDataType,
          subscriptions: personState.company.subscriptions as Subscription[],
          updateStudent: updateStudent,
          onSubmit: handleSubmitLaunch
        })
        // TODO: ADD A NEW FUNCTION TO ALLOW THEM TO LAUNCH ON BEHALF OF OTHERS
      } else {
        handleContent(LAUNCH_FOR_SELF, {
          studentData: student as StudentDataType,
          subscriptions: personState.company.subscriptions as Subscription[],
          updateStudent: updateStudent,
          onSubmit: handleSubmitLaunch
        });
      }
    }
  }
  const handleSubmitLaunch = async (request: LaunchRequest) => {
    if (accessToken) {
      // Sleep for a simulated 10 seconds if intent is to send an email
      const { lid } = decodedToken(accessToken);
      const resposne = await fetch(`${window.__env.API_URL}/api/session/send`, {
        method: "post",
        headers: {
          Pid: request.launchPerson!,
          Lid: personState.company.defaultLocation?.id ?? lid,
          "Content-Type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          action: request.action,
          type: request.screenTypeParsed,
          language: request.languageParsed,
          studentId: request.studentId,
          email: request.email,
          launchPersonId: personState.id,
          enteredAt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXXX")
        }),
      });
      if (resposne.status >= 400) {
        openModal({ type: "REMOTE_LAUNCH_FAILURE", cancelButtonLabel: "close" })
      }

      if (resposne.status === 202) {
        handleSuccessMessage('success');
        return;
      }

      if (resposne.status === 200) {
        const json = await resposne.json();
        if (request.action == "link") {
          openModal({ type: "LINK_PREPARED", cancelButtonLabel: "close", messageProps: { sessionLink: json.redirectUrl } })
          refetch()
        } else {
          window.location.replace(json.redirectUrl);
          window.sessionStorage.clear();
        }
      }
    }
  };

  const handleSendParentalConsent = async ({ action, studentId, noteOrigin }: ParentalConsentRequest) => {
    if (accessToken) {
      let newWindow: Window | null = null;

      if (action == "local") {
        newWindow = window.open("", "_blank");
      }
      const { pid, lid } = decodedToken(accessToken ?? '');
      const response = await fetch(`${window.__env.API_URL}/api/parent/consent/send`, {
        method: "POST",
        headers: {
          Pid: pid,
          Lid: lid,
          "Content-Type": "application/json",
          authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          action: action,
          personId: pid,
          studentId: studentId,
          companyId: personState.company.id,
          NoteOrigin: convert_enum_to_string(origin)
        })
      })
      if (response.status >= 400) {
        openModal({ type: "PARENTAL_CONSENT_ERROR", cancelButtonLabel: "close" })
      }
      if (response.status === 200) {
        // const newWindow = window.open("", "_blank"); // MUST be sync
        const json = await response.json();
        if (newWindow) { //just guarding it
          newWindow.location.href = json.redirectUrl;
        }
        handleSuccessMessage("signNow")
      }

      if (response.status === 202) {
        //  else {
        refetch()
        // }
        updateStudent({
          ...studentData,
          lastConsentAction: {
            ...studentData.lastConsentAction,
            createdAt: new Date()
          },
          consentEmailSentTimes: studentData.consentEmailSentTimes + 1
        })
        handleSuccessMessage("notification");
        return;
      }
    }
    disableClosingDrawer(false);
    toggleDrawer();
  }
  const handleSuccessMessage = (source: string) => {
    toggleDrawer()
    disableClosingDrawer(false);
    if (source == "notification") {
      openModal({ type: "PARENTAL_CONSENT_SENT", eventType: "DISABLE_BACKDROP", cancelButtonLabel: "Close" })
    } else if (source == "signNow") {
      openModal({ type: "PARENTAL_CONSENT_LOCAL", eventType: "DISABLE_BACKDROP", cancelButtonLabel: "Close" })
    } else if (source == "success") {
      refetch()
      openModal({ type: "REMOTE_LAUNCH_SUCCESS", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
    }
  }

  const handleMakeReferral = () => {
    changeStudent({ id: params.studentId, parentEmail: data?.student.parentEmail });
    handleContent(MAKE_A_REFERRAL, {
      studentId: params.studentId
    })
  }
  const handleSendToParent = () => {
    const completedSessions = data?.student.sessions.filter((e) => e.status === "Complete")

    const lastCompleteSession = completedSessions?.length
      ? completedSessions.reduce((newest, current) =>
        new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest
      )
      : null;
    changeStudent({ id: params.studentId, parentEmail: data?.student.parentEmail, lastSession: lastCompleteSession });
    handleContent(SEND_TO_PARENT_OPTIONS, {
      studentData: studentData,
      updateStudent: updateStudent,
      subscriptions: personState.company.subscriptions as Subscription[],
      onAction: handleSend
    })

  }
  const handleSend = async (action: PARENT_OPTION_ACTION, type: PREVIEW_EMAIL_INFO | PARENT_EMAIL_ACTION | TRUESAGE_FILE | string, sendDocs: TRUESAGE_FILE[]) => {
    const { pid, lid } = decodedToken(accessToken ?? '');
    switch (action) {
      case 'send':
        switch (type) {
          case "general":
            var response = await fetch(`${window.__env.API_URL}/api/parent/send/general-information`, {
              method: "post",
              headers: {
                Pid: pid,
                Lid: lid,
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                personId: pid,
                studentId: studentData.id,
              }),
            })
            if (response.status >= 400) {
              showFailureMessage();
              disableClosingDrawer(false)
              return;
            }

            showSuccessMessage();
            toggleDrawer()

            disableClosingDrawer(false)
            break;
          case "report1":
            const parentFiles = studentData.lastSession.report.filter((e: { category: string }) => e.category == "Parent-Report")
            //@ts-ignore
            const newestParentFile = parentFiles?.reduce((newest, current) => {
              return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
            })
            var response = await fetch(`${window.__env.API_URL}/api/parent/send/${newestParentFile.id}`, {
              method: "post",
              headers: {
                Pid: pid,
                Lid: lid,
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                personId: pid,
                studentId: studentData.id,
              }),
            })
            if (response.status >= 400) {
              showFailureMessage();
              disableClosingDrawer(false)
              return;
            }
            showSuccessMessage();
            toggleDrawer()

            disableClosingDrawer(false)
            break;
          case "TrueSage":
            var response = await fetch(`${window.__env.API_URL}/api/parent/send/enlightn`, {
              method: "post",
              headers: {
                Pid: pid,
                Lid: lid,
                "Content-Type": "application/json",
                authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                personId: pid,
                studentId: studentData.id,
                files: sendDocs
              }),
            })
            if (response.status >= 400) {
              showFailureMessage();
              disableClosingDrawer(false)
              return;
            }

            break;
          case "report2":
          default:
            break;
        }
        break;
      default:
        const link = document.createElement('a');
        switch (type) {
          case "preview-general": {
            link.href = `${window.__env.API_URL}/api/file/download/generalParentInfo`
            break;
          }
          case "preview-report1": {
            const parentFiles = studentData.lastSession.report.filter((e: { category: string }) => e.category == "Parent-Report")
            //@ts-ignore
            const newestParentFile = parentFiles?.reduce((newest, current) => {
              return new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest;
            })
            link.href = `${window.__env.API_URL}/api/file/download/client/parent-reports/${newestParentFile.id}`
            break;
          }
          default: {
            link.href = `${window.__env.API_URL}/api/file/download/enlightn/${type}`;
          }
        }
        link.target = "_blank"
        // Prevent potential memory leak and cleanup ObjectURL
        // resources that are no longer in use
        const linkHandler = () => {
          setTimeout(() => {
            link.removeEventListener('click', linkHandler);
            document.body.removeChild(link);
          }, 300);
        }

        link.addEventListener('click', linkHandler);
        document.body.appendChild(link);
        link.click()
        break;
    }
  }
  const showSuccessMessage = () => {
    toggleDrawer();
    disableClosingDrawer(false);
    openModal({ type: "PARENT_SEND_GENERAL_INFORMATION_SUCCESS", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
  };

  const showFailureMessage = () => {
    toggleDrawer();
    disableClosingDrawer(false);
    openModal({ type: "GENERAL_ERROR", eventType: 'DISABLE_BACKDROP', cancelButtonLabel: "Close" });
  };

  const handleGoToSummaryNotes = async () => {
    await changeStudent({ id: params.studentId });
    navigate(`/manage/children/notes/${params.studentId}`)
  }
  const handleGoToDetails = () => {
    navigate(`/manage/children/details/${params.studentId}`)
  }
  const columns = useMemo((): GridColDef[] => {
    return [
      {
        field: 'mark',
        headerName: 'Mark',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        headerAlign: 'center',
        align: 'center',
        width: 75,
        renderHeader: () => (
          <Typography data-tour="review-session">Mark</Typography>
        ),
        renderCell(params) {
          return (
            <Box sx={{ alignContent: "center" }} >
              <MarkSessionCell
                id={params.row.worklist?.id}
                value={params.row.worklist?.status}
                handleMark={handleMark} />
            </Box>
          )
        }
      },
      {
        field: 'sessionType',
        headerName: 'Session Type',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        flex: 1,
        minWidth: 100,
        renderCell(params) {
          return <div dangerouslySetInnerHTML={{ __html: params.row?.sessionType.replaceAll('\n', '<br />') }}></div>
        }
      },
      {
        field: 'launchedBy',
        headerName: 'Launched',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        flex: 1.5,
        renderCell(params) {
          return <div dangerouslySetInnerHTML={{ __html: params.row?.launchedBy.replaceAll('\n', '<br />') }}></div>
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        flex: 1.5,
        renderCell(params) {
          return <div dangerouslySetInnerHTML={{ __html: params.row?.status.replace('\n', '<br />') }}></div>
        }
      },
      {
        field: 'checkpoint',
        headerName: 'Completed Up To',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        // headerAlign: "center",
        minWidth: 90,
        flex: 1,
        // renderHeader: () => (
        //   <div style={{ whiteSpace: "break-spaces", fontWeight: 500, lineHeight: 1.5, textAlign: "center" }}>Completed<br />Up To</div>
        // ),
        renderCell(params) {
          const checkpoint = params.row?.checkpoint
          const sessionType = params.row?.sessionTypeRaw
          return (
            <CheckpointCell checkpoint={checkpoint} sessionType={sessionType} />
          )
        }
      },
      {
        field: 'flag',
        headerName: 'Brief Summary',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        headerAlign: 'center',
        align: 'left',
        flex: 3,
        renderHeader: () => (
          <Typography data-tour="brief-summary">Brief Summary</Typography>
        ),
        renderCell(params) {
          const goToKeywordPage = () => {
            navigate(`/dashboard/student/${params.row?.studentId}/session/${params.row?.id}/keyword`)
          }
          return (
            <TagsCell flags={params.row?.data
              ? JSON.parse(params.row?.data)?.tags
              : null} status={params.row?.worklist?.status} hasKeyWords={params.row?.hasKeyWords} goToKeyWordPage={goToKeywordPage} />
          )
        }
      },
      {
        field: 'view',
        headerName: 'View / Download',
        sortable: false,
        resizable: false,
        editable: false,
        disableColumnMenu: true,
        headerAlign: 'center',
        align: 'center',
        width: 100,
        renderHeader: () => (
          <div data-tour="view-session" style={{ whiteSpace: "break-spaces", fontWeight: 500, lineHeight: 1.5, textAlign: "center" }}>View / Download</div>
        ),
        renderCell(params) {
          return (
            <ViewReportCell value={params.row} />
          )
        }
      },
      // {
      //   field: 'note',
      //   headerName: 'Add Note',
      //   sortable: false,
      //   resizable: false,
      //   editable: false,
      //   disableColumnMenu: true,
      //   headerAlign: 'center',
      //   align: 'center',
      //   width: 100,
      //   renderCell(params) {
      //     return (
      //       <AddNoteCell value={params.row} roles={roles} />
      //     )
      //   }
      // },
      // {
      //   field: 'history',
      //   headerName: 'History',
      //   sortable: false,
      //   resizable: false,
      //   editable: false,
      //   disableColumnMenu: true,
      //   headerAlign: 'center',
      //   align: 'center',
      //   width: 100,
      //   renderCell(params) {
      //     return (
      //       <HistoryCell
      //         data={params.row}
      //         onClick={() => {
      //           setSession(params.row);
      //           onSelectedSession(params.row.studentId, params.row.id)
      //         }}
      //       />
      //     )
      //   }
      // },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectedSession, handleMark]);

  if (error?.networkError)
    return <NetworkError error={true} loading={false} />
  // const headerActions: HeaderActionType = [
  //   {}
  // ]
  return (
    <LoadingWrapper loading={studentSessionsLoading && !data} sx={{ marginTop: 4 }}>
      <Box component="div" data-tour="reports-table" sx={{ background: '#fff', paddingTop: 1, marginTop: 4, marginBottom: 8 }}>
        <Table
          loading={studentSessionsLoading}
          renderToolbar={() => {
            return (
              <Box component="div" sx={{ paddingBottom: 1 }}>

                <SimpleToolbar title={<>
                  Results for {data?.student.firstName} {data?.student.lastName}
                  <Button data-tour="student-info"
                    color="secondary"
                    variant="text"
                    startIcon={<FolderShared />}
                    onClick={() => handleGoToDetails()}
                    sx={{ p: [1.5, 3] }}
                  >Student Info</Button>
                </>}
                  onBack={onBack}
                  rightSlot={() =>
                    canLaunchSingle(roles) ? (<Box component="div" sx={{ display: "flex", justifyContent: "end", flexWrap: "wrap", alignItems: "center" }}>
                      <Tooltip title={data?.student.expired ? "Student is Expired" : ""}>
                        <Box>
                          <Button
                            color="secondary" data-tour="launch-report"
                            variant="text"
                            disabled={data?.student.expired}
                            startIcon={<Launch />}
                            onClick={() => handleLaunch()}
                            sx={{ p: [1.5, 3] }}
                          >
                            Launch
                          </Button>
                        </Box>
                      </Tooltip>
                      {canMakeReferral(roles) ? (<Button data-tour='report-referral'
                        color="secondary"
                        variant="text"

                        startIcon={<Send />}
                        onClick={() => handleMakeReferral()}
                        sx={{ p: [1.5, 3], }}
                      >
                        Referral
                      </Button>) : (<></>)}

                      <Button color="secondary" data-tour="summary-notes"
                        variant='text'
                        startIcon={<Note />}
                        sx={{ p: [1.5, 3] }}
                        onClick={() => handleGoToSummaryNotes()}>
                        Summary Notes
                      </Button>
                      <Button data-tour="parent-resources"
                        color="secondary"
                        variant="text"
                        startIcon={<Send />}
                        onClick={() => handleSendToParent()}
                        sx={{ p: [1.5, 3] }}
                      >
                        Resources for Parents
                      </Button>
                    </Box>) : (<></>)

                  }
                />
              </Box>
            )
          }}
          columns={columns}
          rows={rows}
          pageSize={10}
          disableBoxShadow={false}
          autoHeight />
      </Box>

      <Modal
        open={!!markedId}
        onClose={() => setMarkedId(null)}
      >
        <Box sx={modalstyle} component="div">
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: "center" }}>
            Mark this session as reviewed?
          </Typography>
          <Box component="div" sx={{ paddingTop: "16px", display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Button variant="contained" disableElevation color="inherit" onClick={() => setMarkedId(null)}>
              Cancel
            </Button>
            {loading ?
              (
                <LoadingButton loading variant="contained">
                  Confirm
                </LoadingButton>
              )
              : (<Button variant="contained" disableElevation onClick={() => {
                MarkReviewed({
                  variables: {
                    id: markedId
                  },
                  update: (cache, { data: worklist }) => {
                    const currentSessions = cache.readQuery({
                      query: STUDENT_SESSIONS_QUERY, variables: {
                        studentId: params.studentId
                      }
                    });

                    const nextSessions = {
                      student: {
                        ...currentSessions?.student,
                        sessions: currentSessions?.student.sessions.map(session => session.id === worklist.id
                          ? ({
                            ...session,
                            worklist: {
                              ...session.worklist,
                              ...worklist
                            }
                          })
                          : session
                        )
                      }
                    }

                    cache.writeQuery({
                      query: STUDENT_SESSIONS_QUERY,
                      variables: {
                        studentId: params.studentId
                      },
                      data: nextSessions
                    })
                  },
                }).then((res: any) => {

                  setMarkedId(null)
                })
              }}>
                Confirm
              </Button>)}
          </Box>
        </Box>
      </Modal>
    </LoadingWrapper >
  );
}
